/**
 * Align Tags Tool
 *
 * Aligns the X coordinate of at least two selected tags onto a vertical
 * reference line dropped under the cursor (BIM's "Align Tags"). The tool
 * is activated with tags already selected; one click places the reference.
 */
import { BaseTool } from './BaseTool';
import { ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';
import { getSelectedElements } from '../utils/contextualModelUtils';
import { TagElement } from './TagTool';
import i18n from '../i18n';

export class AlignTagsTool extends BaseTool {
    id = 'align_tags';
    name = 'Align Tags';
    cursor = 'crosshair';

    private selectedTags: string[] = [];
    private referencePoint: THREE.Vector3 | null = null;

    protected onActivate(): void {
        // Resolve the tag subset from the merged selection (both stores), so
        // tags picked in either the viewport or the browser are honored.
        this.selectedTags = getSelectedElements()
            .filter(el => el.category === 'Tags')
            .map(el => el.id);

        if (this.selectedTags.length < 2) {
            this.notify(i18n.t('tools.notify.alignTagsSelectAtLeast2'), 'warning');
            this.exitToSelect();
            return;
        }
        this.notify(i18n.t('tools.notify.alignTagsDrop', { count: this.selectedTags.length }));
    }

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;
        if (this.selectedTags.length < 2) return;

        const point = this.getIntersection(event);
        if (!point) return;

        this.referencePoint = this.snapToGrid(point);

        // Perform alignment, then hand the viewport back to Select.
        this.alignTags();
        this.exitToSelect();
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context || this.selectedTags.length < 2) return;
        const point = this.getIntersection(event);
        if (point) {
            // Vertical reference preview at the cursor X.
            const snapped = this.snapToGrid(point);
            this.context.appStore.setToolPreview({
                type: 'line',
                start: new THREE.Vector3(snapped.x, snapped.y - 10, snapped.z),
                end: new THREE.Vector3(snapped.x, snapped.y + 10, snapped.z),
            });
        }
    }

    onPointerUp(): void { }

    private alignTags() {
        if (!this.context || !this.referencePoint) return;

        const referenceX = this.referencePoint.x;
        let moved = 0;
        this.selectedTags.forEach(id => {
            const el = this.context!.bimStore.project.elements.get(id) as TagElement | undefined;
            if (!el?.geometryData?.position) return;
            const newPos: [number, number, number] = [...el.geometryData.position];
            newPos[0] = referenceX; // Align X

            this.context!.bimStore.updateElement(id, {
                geometryData: { ...el.geometryData, position: newPos }
            } as any);
            moved++;
        });

        this.notify(i18n.t('tools.notify.alignTagsDone', { count: moved }), 'success');
    }
}
